import { useRouter } from "next/dist/client/router";
import React, { useEffect, useRef } from "react";
import useSearchParams from "@/components/hooks/useSearchParams";
import useTabs from "@/components/hooks/useTabs";
import { createBrowserHistory } from "history";
import { Category } from "@/assets/interfaces";
import classNames from "classnames";
import Dropdown, { DropdownItem } from "@/components/ui/dropdown";

interface Props {
  categories: Category[];
  itemsLoading: boolean;
  resetPage: VoidFunction;
}

const SwitchCategories: React.FC<Props> = ({ categories, itemsLoading, resetPage }) => {
  const { category } = useSearchParams(["category"]);
  categories = [{ name: "All", emoji: "" }, ...categories];
  const { tabs, isActive, active, switchTab } = useTabs(
    categories.map((c) => c.name),
    0
  );
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const history = createBrowserHistory();
    const searchParams = new URLSearchParams(window.location.search);
    if (active > 0) {
      searchParams.set("category", categories[active]?.id);

      history.push({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    } else {
      if (!isFirstLoad.current) {
        searchParams.delete("category");

        history.push({
          pathname: location.pathname,
          search: Array.from(searchParams.keys()).length > 0 ? `?${searchParams.toString()}` : undefined,
        });
      }
    }

    if (!isFirstLoad.current) {
      resetPage();
    }

    isFirstLoad.current = false;
  }, [active]);

  useEffect(() => {
    //update selected tab when page loads wit a category query
    const categoryIndex = categories.findIndex((c) => c.id === category);

    if (categoryIndex !== -1 && categoryIndex !== active) {
      switchTab(categoryIndex);
    }
  }, [category]);

  if (categories.length <= 1) {
    return null;
  }

  const dropdownItems: DropdownItem[] = tabs.map((cat, index) => ({
    text: cat,
    link: undefined,
    onClick: () => switchTab(index),
    icon: (
      <figure className="h-6 w-6 bg-primary-500 bg-opacity-10 rounded-full flex items-center justify-center mr-1.25 text-xs">
        {categories[index]?.emoji || "📂"}
      </figure>
    ),
  }));

  return (
    <div className="flex justify-between items-center  mb-5 sm:mb-4.5 lg:mb-5">
      <h3 className="text-black text-1sm sm:text-base font-bold flex items-center font-display">Products</h3>
      <Dropdown items={dropdownItems} className="overflow-ellipsis" vPosition="BOTTOM" maxHeight={350}>
        {(isOpen) => (
          <button
            className={classNames(
              "px-4.5 py-3 md:px-4.5 md:py-3 flex items-center dropdown-toggle gap-2 relative rounded-15 border border-grey-outline no-outline border-opacity-50 bg-white text-black-secondary"
            )}
          >
            <span className="text-1xs">{active == 0 ? "All Categories" : categories[active].name}</span>

            {/* prettier-ignore */}
            <svg className={classNames(`transition-transform text-dark transform flex-shrink-0 w-3 sm:w-4`)} viewBox="0 0 16 17" fill="none">
              <path d="M4.66669 10.5L8.00002 13.8333L11.3334 10.5M4.66669 6.5L8.00002 3.16666L11.3334 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </Dropdown>
    </div>
  );
};

export default SwitchCategories;
